import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { FormattedMessage } from 'react-intl'

import * as gameSelectors from '../game.selectors'
import * as savePointSelectors from '../../SavePoint/savePoint.selectors'
import * as videoPlayerActions from '../../VideoPlayer/videoPlayer.actions'
import * as audioActions from '../AudioContainer/audio.actions'
import * as authSelectors from '../../Auth/auth.selectors'
import { useGameTransition } from '../../hooks/useGameTransition'

import BackgroundsContainer from './BackgroundsContainer'
import VideoPlayer from '../../VideoPlayer'

/**
 *  Scene manager
 */
const SceneContainer = () => {
  const dispatch = useDispatch()
  const [transition, setTransition] = useState(false)
  const { isPending, executeDelayedTransition } = useGameTransition()

  // Redux selectors optimizados
  const sceneData = useSelector(
    (state) => ({
      savePoint: savePointSelectors.getSavePoint(state),
      scene: gameSelectors.getCurrentScene(state),
      userInfo: authSelectors.getUserInfo(state),
    }),
    shallowEqual
  )

  const { savePoint, scene, userInfo } = sceneData

  // Action creators
  const showMedia = useCallback((mediaId) => {
    dispatch(videoPlayerActions.showMedia(mediaId))
  }, [dispatch])

  const addAudio = useCallback((soundId) => {
    dispatch(audioActions.addAudio(soundId))
  }, [dispatch])

  const removeAudio = useCallback((soundId) => {
    dispatch(audioActions.removeAudio(soundId))
  }, [dispatch])

  // Helper function for delayed state updates using React 18 transitions
  const setStateDelayed = useCallback((newState, time) => {
    executeDelayedTransition(() => {
      setTransition(newState.transition)
    }, time)
  }, [executeDelayedTransition])

  // Load media function
  const loadMedia = useCallback(() => {
    const userLang = userInfo && userInfo.licenseData.lang ? userInfo.licenseData.lang : 'en'
    if (scene && scene.media) {
      if (typeof scene.media === 'string') {
        showMedia(scene.media)
      } else {
        if (scene.media[userLang] || scene.media['en']) {
          scene.media[userLang] ? showMedia(scene.media[userLang]) : showMedia(scene.media['en'])
        }
      }
    }
  }, [scene, userInfo, showMedia])

  // Effect for component mount and scene changes
  useEffect(() => {
    if (scene) {
      if (scene.sounds) {
        scene.sounds.forEach((soundId) => addAudio(soundId))
      }
      loadMedia()

      // Transition and message in change scene
      if (scene.transitionText) {
        const delay = scene.transitionDelay ? scene.transitionDelay : 3000
        setStateDelayed({ transition: 'transition-hidden' }, delay)
        setStateDelayed({ transition: 'display-none' }, delay + 1500)
      }
    }

    // Cleanup function
    return () => {
      if (scene && scene.sounds) {
        scene.sounds.forEach((soundId) => removeAudio(soundId))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene])

  // Effect for media loading when scene updates
  useEffect(() => {
    if (scene) {
      loadMedia()
    }
  }, [scene, loadMedia])

  // Render logic - sin returns tempranos
  const userLang = userInfo && userInfo.licenseData.lang ? userInfo.licenseData.lang : 'en'
  let media

  // Determinar media si la escena la tiene
  if (scene && scene.media) {
    if (typeof scene.media === 'string') {
      media = scene.media
    } else {
      if (scene.media[userLang] || scene.media['en']) {
        media = scene.media[userLang] ? scene.media[userLang] : scene.media['en']
      }
    }
  }

  // Renderizado condicional sin returns tempranos
  if (!scene) {
    return <div className="scene-container" />
  }

  if (media) {
    const status = savePoint.elementsStatuses[scene.id]
      ? savePoint.elementsStatuses[scene.id]
      : 'default'
    const videoOptions =
      scene.videoOptions && scene.videoOptions[status]
        ? scene.videoOptions[status]
        : { classController: 'default-controllers' }

    return <VideoPlayer videoOptions={videoOptions} mediaId={media} />
  }

  return (
    <div className="scene-container">
      <BackgroundsContainer />
      {scene.transitionText ? (
        <div
          className={`z-index-99999 empty-black fill-parent transition-scene  ${
            transition ? transition : ''
          } ${isPending ? 'transition-pending' : ''}`}
        >
          <b className="absolute-centered font-medium uppercase">
            <FormattedMessage id={scene.transitionText} />
          </b>
          {isPending && (
            <div className="transition-indicator" style={{
              position: 'absolute',
              bottom: '20px',
              right: '20px',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '12px'
            }}>
              Cargando...
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default React.memo(SceneContainer)
